import { Component } from '@theme/component';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Gets the ordered, de-duplicated list of media IDs present in a gallery root.
   * @param {Element} root - The gallery root to scan (this element or a freshly parsed one).
   * @returns {string[]} The media IDs, in document order.
   */
  #mediaIds(root) {
    return [...root.querySelectorAll('[data-media-id]')]
      .map((el) => /** @type {HTMLElement} */ (el).dataset.mediaId ?? '')
      .filter((id, index, ids) => id && ids.indexOf(id) === index);
  }

  /**
   * Handles a variant:selected event by instantly jumping to the matching slide,
   * without waiting for the section fetch to resolve. `product_option_value.variant`
   * may not always match the fully resolved combination for multi-option products;
   * #handleVariantUpdate corrects the slide afterwards once the server response
   * arrives, without rebuilding the DOM, so any mismatch here is not visible.
   *
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    const optionValueId = event.detail?.resource?.id;
    if (!optionValueId) return;

    const input = this.closest('.shopify-section, dialog')?.querySelector(
      `variant-picker input[data-option-value-id="${optionValueId}"]`
    );
    const mediaId = /** @type {HTMLElement | null} */ (input)?.dataset.mediaId;
    if (!mediaId) return;

    const index = this.#mediaIds(this).indexOf(mediaId);
    if (index >= 0) this.slideshow?.select(index, undefined, { animate: false });
  };

  /**
   * Handles a variant update event. When the new gallery contains the same set of
   * media as the current one (the server only reordered them to put the variant's
   * image first), the existing DOM is kept and the slideshow just jumps to the
   * right slide — no images are re-created or re-decoded. Only when the media set
   * actually changes (combined listings, `hide_variants`) is the gallery replaced.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const currentIds = this.#mediaIds(this);
    const newIds = this.#mediaIds(newMediaGallery);
    const sameMediaSet =
      currentIds.length > 0 && currentIds.length === newIds.length && newIds.every((id) => currentIds.includes(id));

    if (sameMediaSet) {
      const index = currentIds.indexOf(newIds[0] ?? '');
      if (index >= 0) this.slideshow?.select(index, undefined, { animate: false });
      return;
    }

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
